/**
 * Resolves a tab query parameter (e.g. `?show=Campaigns`) to a member of a numeric ViewTabsType
 * enum, falling back to `defaultTab` for anything that is not a real member.
 *
 * The fallback is not cosmetic. The previous `+ViewTabsType[searchParams.get('show') as any]`
 * produced NaN for an unrecognised value, and `<Tabs value={NaN}>` re-renders forever — NaN is
 * never equal to itself, so MUI's internal value sync never settles — until React aborts the
 * whole tree with "Too many re-renders" and the page white screens. A tab index that simply has
 * no matching <Tab> renders fine, so keeping the value a real number is what matters.
 *
 * Values that must not resolve to a tab:
 *  - names that are not in the enum ('bogus'),
 *  - the enum's own reverse mapping ('1' answers with the name 'Campaigns', not an index),
 *  - keys inherited from Object.prototype ('constructor', 'toString', '__proto__').
 */
export const resolveTabIndex = (
  viewTabs: Record<string, string | number>,
  showParam: string | null,
  defaultTab: number,
): number => {
  if (showParam === null) {
    return defaultTab;
  }

  // hasOwnProperty rather than `in`, so inherited keys don't resolve to a function.
  if (!Object.prototype.hasOwnProperty.call(viewTabs, showParam)) {
    return defaultTab;
  }

  const tabIndex = viewTabs[showParam];

  // Numeric enums map both ways, so viewTabs['1'] hands back a name rather than an index.
  return typeof tabIndex === 'number' ? tabIndex : defaultTab;
};
